import type { ReactElement } from 'react';
import type { GetFanClubStatusResponse, ShowSummary, ShowViewerRole } from '@fetmode/types';
import type { createApiClient } from '@fetmode/api-client';
import { ApiError } from '@fetmode/api-client';
import { t } from '@fetmode/i18n';
import { Button } from '@fetmode/ui';

type ApiClient = ReturnType<typeof createApiClient>;

export interface ShowTypePanelProps {
  show: ShowSummary;
  showId: string;
  username: string;
  user: { id: string } | null;
  api: ApiClient;
  viewerRole: ShowViewerRole | null;
  fanClub: GetFanClubStatusResponse | null;
  isJoining: boolean;
  onJoiningChange: (joining: boolean) => void;
  onViewerRoleChange: (role: ShowViewerRole) => void;
  onViewerCountChange: (count: number) => void;
  onShowRefresh: () => Promise<void>;
  onError: (message: string | null) => void;
  onCoinBalanceChange: (total: number) => void;
  onFanClubChange: (status: GetFanClubStatusResponse) => void;
  onManualJoin?: () => void;
}

function handleJoinError(err: unknown, onError: (message: string | null) => void): void {
  onError(err instanceof ApiError ? err.message : 'Unable to join this show.');
}

export function ShowTypeAccessPanel({
  show,
  showId,
  username,
  user,
  api,
  viewerRole,
  fanClub,
  isJoining,
  onJoiningChange,
  onViewerRoleChange,
  onViewerCountChange,
  onShowRefresh,
  onError,
  onCoinBalanceChange,
  onFanClubChange,
  onManualJoin,
}: ShowTypePanelProps): ReactElement | null {
  const handleJoinShow = (joinAs?: ShowViewerRole): void => {
    onManualJoin?.();
    onJoiningChange(true);
    onError(null);

    void api
      .joinShow(showId, joinAs ? { joinAs } : {})
      .then(async (result) => {
        onViewerCountChange(result.viewerCount);
        onViewerRoleChange(result.joinedRole);
        onError(null);
        await onShowRefresh();
      })
      .catch((err: unknown) => {
        handleJoinError(err, onError);
      })
      .finally(() => {
        onJoiningChange(false);
      });
  };

  const showSpyJoin =
    Boolean(user) &&
    show.isViewer &&
    show.type !== 'VIP' &&
    show.type !== 'EXCLUSIVE' &&
    show.type !== 'PEEP' &&
    show.access.spyEnabled &&
    show.access.spyCoinsPerMinute &&
    viewerRole !== 'SPY' &&
    show.status === 'LIVE';

  const showFanClubBadge = Boolean(fanClub?.isMember && show.access.requiresFanClub);

  if (!showFanClubBadge && !showSpyJoin) {
    return null;
  }

  return (
    <div className="show-room__type-access">
      {showFanClubBadge ? (
        <span className="show-room__type-access-badge">{t('common.show.fanClubMember')}</span>
      ) : null}

      {showSpyJoin ? (
        <Button
          label={t('common.show.joinSpy').replace(
            '{{amount}}',
            String(show.access.spyCoinsPerMinute),
          )}
          variant="secondary"
          disabled={isJoining}
          onClick={() => {
            handleJoinShow('SPY');
          }}
        />
      ) : null}
    </div>
  );
}
